/**
 * THE PORTAL, DRAWN ONCE.
 *
 * The member's phone and the Command Center's live preview render THIS
 * component, so the preview cannot drift from what a member sees. The portal
 * page passes real data and real handlers; the preview passes a draft design
 * and sample data, with `preview` so nothing can be submitted.
 *
 * NO PER-SCREEN DESIGN CODE: every button and every editable line is read from
 * the `slots` list the server sends (services/loyalty/design.py). A new slot
 * appears here, in the editor and in the preview without a line of new code.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, Phone } from 'lucide-react';
import { BackBar, BigButton, Field, Light, Logos, Screen, Title } from './ui';
import { GiftCardFace, GiftScreens } from './GiftScreens';
import { BUTTON_ICONS } from './buttonIcons';
import { money as sharedMoney, moneyFieldProps, plain } from '../../lib/money';

const money = (v) => sharedMoney(v);

const maskEmail = (email) => {
    const [name, domain] = String(email || '').split('@');
    if (!domain) return email || '';
    return `${name.slice(0, 1)}***@${domain}`;
};

export default function PortalView({
    screen, cfg = {}, me = null, form = {}, error = '', busy = false, gifts = null,
    saved = null, askRemember = false, notice = '', on = {}, framed = false, preview = false,
}) {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const branding = cfg.design?.branding || {};
    // The clinic's look travels with the theme, so EVERY button of the portal
    // follows it — including the few that carry no colour slot of their own.
    const theme = { ...(cfg.design?.theme || {}), lux_buttons: !!branding.lux_buttons };
    // In the preview the lights are shown with sample numbers, never real ones.
    const lights = branding.desk_lights !== false;
    const sampleLight = (id) => (lights && id === 'desk_pending_btn'
        ? <Light count={2} testid="loyalty-sample-light-pending" /> : null);
    const programName = me?.program_name || cfg.program_name || '';
    const firstName = me?.member?.first_name || saved?.first_name || form.first_name || '';

    const slots = useMemo(() => {
        const map = {};
        (cfg.slots || []).forEach((s) => { map[s.id] = s; });
        return map;
    }, [cfg.slots]);

    /** Organization's own wording for the member's language, else the industry
     *  profile's wording, else the engine's — always in the SAME language. */
    const text = (id) => {
        const slot = slots[id];
        if (!slot) return '';
        const own = slot.label?.[lang];
        const base = own
            || (slot.dynamic === 'program_name' ? programName : '')
            || (slot.profile_key ? t(slot.profile_key, '') : '')
            || t(slot.default_key, '');
        return String(base).replace('{{name}}', firstName);
    };
    const shown = (id) => Boolean(slots[id]?.visible);
    const paint = (id) => ({
        bg: slots[id]?.bg || '', fg: slots[id]?.fg || '',
        icon: branding.show_icons ? BUTTON_ICONS[id] : null,
        lux: !!branding.lux_buttons,
    });
    // "Gift cards only" hides every points figure from the member.
    const showPoints = me?.show_points ?? cfg.show_points ?? true;

    const go = (next) => () => (preview ? null : on.go?.(next));
    const set = (key) => (e) => on.setForm?.({ ...form, [key]: e.target.value });
    const setPin = (e) => on.setForm?.({ ...form, pin: e.target.value.replace(/\D/g, '') });

    const errorLine = error
        ? <p className="text-center text-sm text-rose-300" data-testid="loyalty-error">{error}</p>
        : null;
    const spin = <Loader2 className="mx-auto h-5 w-5 animate-spin" />;

    return (
        <Screen testid="loyalty-portal" branding={branding} theme={theme} framed={framed}>
            <Logos branding={branding} />

            {/* THE ORG LOYALTY PORTAL screens, previewed from their own slots so
                the clinic designs the front desk in the same designer. */}
            {String(screen).startsWith('desk_') && (
                <div className="space-y-5" data-testid={`loyalty-${screen}-screen`}>
                    {/* THE THANK-YOU a client sees once the desk has her. */}
                    {screen === 'desk_checked_in' && (
                        <Title theme={theme} testid="loyalty-desk-sample-thanks">
                            {t('loyalty.desk.checkedIn', 'Thank you {{name}} — the desk has you.')
                                .replace('{{name}}', t('loyalty.desk.sampleOne', 'Marie L. (sample)'))}
                        </Title>
                    )}
                    {/* ADD POINTS opens on the client the staff member tapped. */}
                    {screen === 'desk_award' && (
                        <Title theme={theme} testid="loyalty-desk-sample-open">
                            {t('loyalty.desk.sampleOne', 'Marie L. (sample)')}
                        </Title>
                    )}
                    {(cfg.slots || []).filter((s) => s.screen === screen).map((s) => (
                        s.kind === 'text'
                            ? (shown(s.id) && (
                                <div key={s.id} className="space-y-5">
                                    <Title theme={theme} testid={`loyalty-${s.id}`}>
                                        {text(s.id)}
                                    </Title>
                                    {/* The two clients waiting, as the staff sees them. */}
                                    {s.id === 'desk_checkins_title' && (
                                        [['loyalty.desk.sampleOne', 'Marie L. (sample)'],
                                         ['loyalty.desk.sampleTwo', 'Jason P. (sample)']].map(([key, fallback]) => (
                                            <BigButton key={key} preview={preview} theme={theme}
                                                       disabled={preview}
                                                       testid={`loyalty-${key.split('.').pop()}`}>
                                                {t(key, fallback)}
                                            </BigButton>
                                        ))
                                    )}
                                    {/* The box the bill is typed into, under its label. */}
                                    {s.id === 'desk_amount_label' && (
                                        <Field testid="loyalty-desk-sample-amount" theme={theme}
                                               value={money(120)} readOnly />
                                    )}
                                </div>
                            ))
                            : (shown(s.id) && (
                                <BigButton key={s.id} preview={preview} theme={theme}
                                           {...paint(s.id)} disabled={preview}
                                           light={sampleLight(s.id)}
                                           testid={`loyalty-${s.id}`}>
                                    {text(s.id)}
                                </BigButton>
                            ))
                    ))}
                    {/* The check-ins button is on the manager's menu too, with its own light. */}
                    {screen === 'desk_checked_in' && (
                        <BigButton preview={preview} theme={theme} tone="quiet" disabled={preview}
                                   testid="loyalty-desk-sample-close">
                            {t('common.close', 'Close')}
                        </BigButton>
                    )}
                    {screen === 'desk_manager' && (
                        <BigButton preview={preview} theme={theme} tone="quiet" disabled={preview}
                                   light={lights ? <Light count={0}
                                                          testid="loyalty-sample-light-checkins" /> : null}
                                   testid="loyalty-desk-manager-checkins-sample">
                            {text('desk_checkins_title')}
                        </BigButton>
                    )}
                </div>
            )}

            {screen === 'register' && (
                <div className="space-y-5" data-testid="loyalty-register-screen">
                    <Title theme={theme} testid="loyalty-register-title">{text('register_title')}</Title>
                    <Field label={t('loyalty.firstName', 'First name')} testid="loyalty-first-name"
                           value={form.first_name || ''} onChange={set('first_name')} />
                    <Field label={t('loyalty.email', 'Email')} type="email" testid="loyalty-email"
                           value={form.email || ''} onChange={set('email')} />
                    <Field label={t('loyalty.pinChoose', 'Choose a 4-digit PIN')} inputMode="numeric"
                           maxLength={4} testid="loyalty-pin" value={form.pin || ''} onChange={setPin} />
                    <label className="flex items-start gap-3 text-sm opacity-90">
                        <input type="checkbox" checked={Boolean(form.consent)} data-testid="loyalty-consent"
                               onChange={(e) => on.setForm?.({ ...form, consent: e.target.checked })}
                               className="mt-1 h-4 w-4" />
                        <span>
                            {cfg.consent?.text}
                            <span className="mt-1 block text-xs opacity-70" data-testid="loyalty-consent-note">
                                {cfg.consent?.note}
                            </span>
                        </span>
                    </label>
                    {errorLine}
                    {shown('register_submit') && (
                        <BigButton preview={preview} theme={theme} {...paint('register_submit')} disabled={busy || preview}
                                   onClick={() => on.register?.()} testid="loyalty-register-btn">
                            {busy ? spin : text('register_submit')}
                        </BigButton>
                    )}
                    {shown('register_have_account') && (
                        <BigButton preview={preview} tone="quiet" theme={theme} {...paint('register_have_account')}
                                   testid="loyalty-have-account" onClick={go('sign_in')}>
                            {text('register_have_account')}
                        </BigButton>
                    )}
                </div>
            )}

            {screen === 'check_email' && (
                <div className="space-y-5 text-center" data-testid="loyalty-check-email-screen">
                    <Title theme={theme}>{text('check_email_title')}</Title>
                    <p className="text-sm opacity-80">{text('check_email_body')}</p>
                    {errorLine}
                    {shown('check_email_resend') && (
                        <BigButton preview={preview} tone="quiet" theme={theme} {...paint('check_email_resend')}
                                   testid="loyalty-resend-btn"
                                   disabled={busy || preview} onClick={() => on.resend?.()}>
                            {text('check_email_resend')}
                        </BigButton>
                    )}
                </div>
            )}

            {screen === 'confirmed' && (
                <div className="space-y-5 text-center" data-testid="loyalty-confirmed-screen">
                    <Title theme={theme}>{text('confirmed_title')}</Title>
                    <p className="text-sm opacity-80">{text('confirmed_body')}</p>
                    {me?.points != null && (
                        <p className="text-4xl font-bold" data-testid="loyalty-confirmed-points"
                           style={{ color: theme.accent }}>{me.points}</p>
                    )}
                    {shown('confirmed_continue') && (
                        <BigButton preview={preview} theme={theme} {...paint('confirmed_continue')} disabled={preview}
                                   onClick={() => on.continueToPortal?.()} testid="loyalty-confirmed-continue">
                            {text('confirmed_continue')}
                        </BigButton>
                    )}
                </div>
            )}

            {screen === 'sign_in' && (
                <div className="space-y-5" data-testid="loyalty-pin-screen">
                    <Title theme={theme}>
                        {saved?.first_name ? text('welcome_back_title') : text('signin_title')}
                    </Title>
                    {saved?.email ? (
                        <p className="text-center text-sm opacity-80" data-testid="loyalty-masked-email">
                            {maskEmail(saved.email)}
                        </p>
                    ) : (
                        <Field label={t('loyalty.email', 'Email')} type="email" testid="loyalty-email"
                               value={form.email || ''} onChange={set('email')} />
                    )}
                    <Field label={t('loyalty.pinOnly', 'Your 4-digit PIN')} inputMode="numeric"
                           maxLength={4} testid="loyalty-pin" value={form.pin || ''} onChange={setPin} />
                    {notice === 'reset_sent' && (
                        <p className="text-center text-sm opacity-80" data-testid="loyalty-reset-sent">
                            {text('signin_reset_sent')}
                        </p>
                    )}
                    {errorLine}
                    {shown('signin_submit') && (
                        <BigButton preview={preview} theme={theme} {...paint('signin_submit')} disabled={busy || preview}
                                   onClick={() => on.signIn?.()} testid="loyalty-signin-btn">
                            {busy ? spin : text('signin_submit')}
                        </BigButton>
                    )}
                    {shown('signin_forgot_pin') && (
                        <BigButton preview={preview} tone="quiet" theme={theme} {...paint('signin_forgot_pin')}
                                   testid="loyalty-forgot-pin" disabled={preview}
                                   onClick={() => on.forgotPin?.()}>
                            {text('signin_forgot_pin')}
                        </BigButton>
                    )}
                    {saved?.email && (
                        <button type="button" data-testid="loyalty-not-you"
                                onClick={() => (preview ? null : on.notYou?.())}
                                className="w-full text-center text-xs opacity-70">
                            {t('loyalty.notYou', 'Not you?')}
                        </button>
                    )}
                </div>
            )}

            {screen === 'confirm_pin' && (
                <div className="space-y-5" data-testid="loyalty-confirm-pin">
                    <Title theme={theme} testid="loyalty-confirm-pin-title">
                        {text('confirm_pin_title')}
                    </Title>
                    <Field label={t('loyalty.pinOnly', 'Your 4-digit PIN')} inputMode="numeric"
                           maxLength={4} testid="loyalty-pin" value={form.pin || ''} onChange={setPin} />
                    {errorLine}
                    {shown('confirm_pin_submit') && (
                        <BigButton preview={preview} theme={theme} {...paint('confirm_pin_submit')}
                                   disabled={busy || preview} testid="loyalty-confirm-pin-submit"
                                   onClick={() => on.confirmPin?.()}>
                            {busy ? spin : text('confirm_pin_submit')}
                        </BigButton>
                    )}
                </div>
            )}

            {screen === 'pin_reset' && (
                <div className="space-y-5" data-testid="loyalty-pin-reset-screen">
                    <Title theme={theme} testid="loyalty-pin-reset-title">{text('pin_reset_title')}</Title>
                    {saved?.done ? (
                        <p className="text-center text-sm opacity-80" data-testid="loyalty-pin-reset-done">
                            {text('pin_reset_done')}
                        </p>
                    ) : (
                        <>
                            <Field label={t('loyalty.pinOnly', 'Your 4-digit PIN')} inputMode="numeric"
                                   maxLength={4} testid="loyalty-new-pin" value={form.pin || ''}
                                   onChange={setPin} />
                            {errorLine}
                            {shown('pin_reset_submit') && (
                                <BigButton preview={preview} theme={theme} {...paint('pin_reset_submit')}
                                           disabled={busy || preview} testid="loyalty-new-pin-save"
                                           onClick={() => on.setNewPin?.()}>
                                    {busy ? spin : text('pin_reset_submit')}
                                </BigButton>
                            )}
                        </>
                    )}
                    {saved?.done && shown('confirmed_continue') && (
                        <BigButton preview={preview} theme={theme} {...paint('confirmed_continue')} disabled={preview}
                                   testid="loyalty-confirm-enter" onClick={() => on.continueToPortal?.()}>
                            {text('confirmed_continue')}
                        </BigButton>
                    )}
                </div>
            )}

            {screen === 'welcome_back' && (
                <div className="space-y-4 text-center" data-testid="loyalty-welcome-screen">
                    <Title theme={theme}>{text('welcome_back_title')}</Title>
                    {askRemember && (
                        <div className="space-y-3 rounded-2xl bg-white/5 p-4" data-testid="loyalty-remember-ask">
                            <p className="text-sm opacity-90">
                                {t('loyalty.rememberAsk', 'Remember my email on this phone?')}
                            </p>
                            <div className="flex gap-3">
                                <BigButton preview={preview} theme={theme} testid="loyalty-remember-yes"
                                           onClick={() => on.remember?.(true)}>
                                    {t('common.yes', 'Yes')}
                                </BigButton>
                                <BigButton preview={preview} tone="quiet" theme={theme} testid="loyalty-remember-no"
                                           onClick={() => on.remember?.(false)}>
                                    {t('common.no', 'No')}
                                </BigButton>
                            </div>
                        </div>
                    )}
                    {shown('welcome_back_continue') && (
                        <BigButton preview={preview} tone="quiet" theme={theme} {...paint('welcome_back_continue')}
                                   testid="loyalty-skip-welcome" onClick={go('home')}>
                            {text('welcome_back_continue')}
                        </BigButton>
                    )}
                </div>
            )}

            {screen === 'home' && (
                <div className="space-y-4" data-testid="loyalty-home-screen">
                    <Title theme={theme} testid="loyalty-home-title">{text('home_title')}</Title>
                    {shown('home_contact') && (
                        <BigButton preview={preview} theme={theme} {...paint('home_contact')} testid="loyalty-home-contact"
                                   onClick={go('contact')}>{text('home_contact')}</BigButton>
                    )}
                    {shown('home_bookings') && (
                        <BigButton preview={preview} theme={theme} {...paint('home_bookings')} testid="loyalty-home-bookings"
                                   onClick={go('bookings')}>{text('home_bookings')}</BigButton>
                    )}
                    {shown('home_vip') && (
                        <BigButton preview={preview} theme={theme} {...paint('home_vip')} testid="loyalty-home-vip"
                                   onClick={go('vip')}>{text('home_vip')}</BigButton>
                    )}
                    {shown('home_sign_out') && (
                        <button type="button" data-testid="loyalty-sign-out"
                                onClick={() => (preview ? null : on.signOut?.())}
                                className="w-full pt-2 text-center text-xs opacity-70">
                            {text('home_sign_out')}
                        </button>
                    )}
                </div>
            )}

            {screen === 'contact' && (
                <div className="space-y-4" data-testid="loyalty-contact-screen">
                    <BackBar onBack={go('home')} label={t('common.back', 'Back')} />
                    <Title theme={theme}>{text('contact_title')}</Title>
                    {cfg.contact?.email && shown('contact_email') && (
                        <a href={preview ? undefined : `mailto:${cfg.contact.email}`}
                           data-testid="loyalty-contact-email"
                           className="flex items-center justify-center gap-2 rounded-2xl px-6 py-5 text-lg font-semibold"
                           style={{ background: paint('contact_email').bg || theme.button || '#f5a623',
                                    color: paint('contact_email').fg || theme.button_text || '#111827' }}>
                            <Mail className="h-5 w-5" /> {text('contact_email')}
                        </a>
                    )}
                    {cfg.contact?.phone && shown('contact_call') && (
                        <a href={preview ? undefined : `tel:${cfg.contact.phone}`}
                           data-testid="loyalty-contact-call"
                           className="flex items-center justify-center gap-2 rounded-2xl px-6 py-5 text-lg font-semibold"
                           style={{ background: paint('contact_call').bg || 'rgba(255,255,255,.10)',
                                    color: paint('contact_call').fg || undefined }}>
                            <Phone className="h-5 w-5" /> {text('contact_call')}
                        </a>
                    )}
                    {!cfg.contact?.email && !cfg.contact?.phone && (
                        <p className="text-center text-sm opacity-70" data-testid="loyalty-contact-empty">
                            {t('loyalty.contactEmpty', 'No contact details yet')}
                        </p>
                    )}
                </div>
            )}

            {screen === 'vip' && (
                <div className="space-y-4" data-testid="loyalty-vip-screen">
                    <BackBar onBack={go('home')} label={t('common.back', 'Back')} />
                    <Title theme={theme}>{text('vip_title')}</Title>
                    {showPoints && (
                        <div className="rounded-2xl bg-white/5 p-6 text-center">
                            <p className="text-4xl font-bold" data-testid="loyalty-points"
                               style={{ color: theme.accent }}>{me?.points ?? 0}</p>
                            <p className="text-sm opacity-80" data-testid="loyalty-points-label">
                                {text('vip_points_label')}
                            </p>
                            {me?.dollar_value != null && (
                                <p className="mt-3 text-lg" data-testid="loyalty-dollar-value">
                                    {money(me.dollar_value)}
                                </p>
                            )}
                            <p className="mt-2 text-sm opacity-80" data-testid="loyalty-to-cash-in">
                                {me?.points_remaining
                                    ? t('loyalty.morePoints', '{{count}} more points to cash in',
                                        { count: me.points_remaining })
                                    : text('vip_cash_in')}                            </p>
                            {/* WAITING FOR A MANAGER: shown apart, never added to
                                what she can spend. */}
                            {Number(me?.pending_points || 0) > 0 && shown('member_pending_note') && (
                                <p className="mt-3 text-sm opacity-90"
                                   data-testid="loyalty-pending-points">
                                    {(text('member_pending_note') || '')
                                        .replace('{{points}}',
                                            Number(me.pending_points).toLocaleString())}
                                </p>
                            )}
                        </div>
                    )}
                    {shown('vip_cash_in_btn') && (
                        <BigButton preview={preview} theme={theme} {...paint('vip_cash_in_btn')}
                                   disabled={preview} testid="loyalty-cash-in-btn"
                                   onClick={() => (preview ? null : on.go?.('cash_in'))}>
                            {text('vip_cash_in_btn')}
                        </BigButton>
                    )}
                    {showPoints && shown('vip_create_card_btn') && (
                        <BigButton preview={preview} theme={theme} {...paint('vip_create_card_btn')}
                                   testid="loyalty-vip-create-card"
                                   onClick={() => (preview ? null : on.go?.('points_card'))}>
                            {text('vip_create_card_btn')}
                        </BigButton>
                    )}
                    {notice === 'cashed_in' && (
                        <p className="text-center text-sm opacity-90" data-testid="loyalty-cashed-in">
                            {t('loyalty.cashedIn', 'Your gift card is ready in Redeem')}
                        </p>
                    )}
                    {errorLine}
                    {shown('vip_gift_cards') && (
                        <BigButton preview={preview} theme={theme} {...paint('vip_gift_cards')}
                                   testid="loyalty-gift-cards-btn"
                                   onClick={() => (preview ? null : on.go?.('gifts'))}>
                            {text('vip_gift_cards')}
                        </BigButton>
                    )}
                </div>
            )}

            {screen === 'cash_in' && (() => {
                /** REDEEM MY POINTS — she types the amount SHE wants, in
                 *  dollars; the points it costs and her new balance appear at
                 *  once. Nothing moves until Accept. */
                const pointValue = Number(me?.point_value || 0);
                const points = Number(me?.points ?? 0);
                const threshold = Number(me?.points_to_cash_in || 0);
                const unlocked = preview || points >= threshold;
                const low = me?.cash_in_min == null ? null : Number(me.cash_in_min);
                const high = me?.cash_in_max == null ? null : Number(me.cash_in_max);
                const amount = Number(plain(form.value) || 0);
                const cost = pointValue > 0 && amount > 0 ? Math.round(amount / pointValue) : 0;
                const left = points - cost;
                const tooSmall = low !== null && amount > 0 && amount < low;
                const tooBig = high !== null && amount > high;
                const ready = unlocked && amount > 0 && cost > 0 && left >= 0
                    && !tooSmall && !tooBig;
                return (
                    <div className="space-y-4" data-testid="loyalty-cash-in-screen">
                        <BackBar onBack={go('vip')} label={t('common.back', 'Back')} />
                        <Title theme={theme} testid="loyalty-cash-in-title">{text('cash_in_title')}</Title>

                        <div className="rounded-2xl bg-white/5 p-4" data-testid="loyalty-cash-in-balance">
                            <p className="text-sm opacity-80">{text('cash_in_balance')}</p>
                            <p className="text-2xl font-bold" style={{ color: theme.accent }}
                               data-testid="loyalty-cash-in-points">
                                {points.toLocaleString()} · {money(points * pointValue)}
                            </p>
                        </div>

                        {!unlocked && (
                            <div className="rounded-2xl bg-white/5 p-4" data-testid="loyalty-cash-in-locked">
                                <p className="text-sm opacity-90">{text('cash_in_locked')}</p>
                                <p className="mt-1 text-sm opacity-80" data-testid="loyalty-cash-in-remaining">
                                    {t('loyalty.morePoints', '{{count}} more points to cash in',
                                        { count: Math.max(0, threshold - points) })}
                                </p>
                            </div>
                        )}

                        {unlocked && !form.cashed && (
                            <>
                                <Field label={text('cash_in_amount_label')}
                                       testid="loyalty-cash-in-amount"
                                       {...moneyFieldProps(form.value || '',
                                           (next) => on.setForm?.({ ...form, value: next }))} />
                                <p className="text-sm opacity-80" data-testid="loyalty-cash-in-cost">
                                    {t('loyalty.redeemPoints.costs', 'costs {{count}} points',
                                        { count: cost })}
                                </p>
                                {(low !== null || high !== null) && (
                                    <p className="text-xs opacity-70" data-testid="loyalty-cash-in-limits">
                                        {low !== null && t('loyalty.redeemPoints.min',
                                            'Smallest amount {{amount}}', { amount: money(low) })}
                                        {low !== null && high !== null ? ' · ' : ''}
                                        {high !== null && t('loyalty.redeemPoints.max',
                                            'Largest amount {{amount}}', { amount: money(high) })}
                                    </p>
                                )}
                                {amount > 0 && (
                                    <div className="rounded-2xl bg-white/5 p-4" data-testid="loyalty-cash-in-after">
                                        <p className="text-sm opacity-80">{text('cash_in_after')}</p>
                                        <p className="text-xl font-bold"
                                           data-testid="loyalty-cash-in-new-balance">
                                            {left.toLocaleString()} · {money(Math.max(0, left) * pointValue)}
                                        </p>
                                    </div>
                                )}
                            </>
                        )}

                        {errorLine}

                        {!form.cashed && shown('cash_in_submit') && (
                            <BigButton preview={preview} theme={theme} {...paint('cash_in_submit')}
                                       disabled={busy || preview || !ready}
                                       testid="loyalty-cash-in-submit"
                                       onClick={() => on.cashIn?.()}>
                                {busy ? spin : text('cash_in_submit')}
                            </BigButton>
                        )}

                        {form.cashed && (
                            <div className="space-y-3" data-testid="loyalty-cash-in-done">
                                <p className="text-center text-sm opacity-90"
                                   data-testid="loyalty-cash-in-ready">{text('cash_in_ready')}</p>
                                <GiftCardFace card={form.cashed} theme={theme}
                                              testid="loyalty-cash-in-card" />
                                {shown('cash_in_open') && (
                                    <BigButton preview={preview} theme={theme} {...paint('cash_in_open')}
                                               disabled={preview} testid="loyalty-cash-in-open"
                                               onClick={() => (preview ? null : on.go?.('redeem'))}>
                                        {text('cash_in_open')}
                                    </BigButton>
                                )}
                            </div>
                        )}
                    </div>
                );
            })()}

            {['gifts', 'bank', 'give', 'redeem', 'create', 'create_allowance',
              'points_card', 'history'].includes(screen) && (
                <GiftScreens screen={screen} gifts={gifts || cfg.sample_gifts} theme={theme}
                             text={text} shown={shown} paint={paint} form={form} on={on}
                             preview={preview} error={error} busy={busy} />
            )}

            {screen === 'bookings' && (
                <div className="space-y-4" data-testid="loyalty-bookings-screen">
                    <BackBar onBack={go('home')} label={t('common.back', 'Back')} />
                    <p className="text-center text-sm opacity-70">{t('loyalty.comingSoon', 'Coming soon')}</p>
                </div>
            )}
        </Screen>
    );
}
